import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Zap, 
  Plug,
  PlugZap,
  Activity,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  LogOut,
  ArrowLeft,
  Lightbulb,
  Thermometer,
  Tv,
  Gauge
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LinkyPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [meter, setMeter] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dailyDetail, setDailyDetail] = useState(null);
  
  // Form state for connection
  const [connectForm, setConnectForm] = useState({
    name: 'Mon compteur Linky',
    address: '',
    contract_type: 'BASE',
    subscribed_power: '6'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Polling for realtime data every 10 seconds
    if (meter?.connected) {
      const interval = setInterval(fetchRealtime, 10000);
      return () => clearInterval(interval);
    }
  }, [meter]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const meterRes = await axios.get(`${API_URL}/api/linky/meter`, { headers });
      
      if (meterRes.data.connected) {
        setMeter(meterRes.data.meter);
        await fetchConsumption();
        await fetchRealtime();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsumption = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/linky/consumption?days=30`, { headers });
      setConsumption(res.data);
    } catch (error) {
      console.error('Error fetching consumption:', error);
    }
  };

  const fetchRealtime = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/linky/realtime`, { headers });
      setRealtime(res.data);
    } catch (error) {
      console.error('Error fetching realtime:', error);
    }
  };

  const fetchDailyDetail = async (date) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/linky/consumption/${date}`, { headers });
      setDailyDetail(res.data.consumption);
      setSelectedDate(date);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!connectForm.address) {
      toast.error('Veuillez entrer votre adresse');
      return;
    }
    
    setConnecting(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API_URL}/api/linky/connect`, {
        ...connectForm,
        subscribed_power: parseInt(connectForm.subscribed_power)
      }, { headers });
      
      setMeter(res.data.meter);
      toast.success('Compteur Linky connecté !');
      await fetchConsumption();
      await fetchRealtime();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/linky/disconnect`, { headers });
      setMeter(null);
      setConsumption(null);
      setRealtime(null);
      toast.success('Compteur déconnecté');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/linky/sync`, {}, { headers });
      await fetchConsumption();
      toast.success('Données synchronisées');
    } catch (error) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  // Prepare chart data
  const chartData = consumption?.daily_consumption?.map(d => ({
    date: d.date.slice(5), // MM-DD
    total: d.total_kwh,
    peak: d.peak_kwh,
    offPeak: d.off_peak_kwh
  })) || [];

  const curveData = dailyDetail?.points?.map(p => ({
    time: p.timestamp.slice(11, 16),
    kwh: p.value_kwh
  })) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/linky" className="nav-link active">Linky</Link>
              <Link to="/forecasts" className="nav-link">Prévisions</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <PlugZap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    Compteur Linky
                  </h1>
                  <p className="text-muted-foreground">
                    {meter ? 'Suivi de votre consommation en temps réel' : 'Connectez votre compteur'}
                  </p>
                </div>
              </div>
            </div>
            
            {meter && (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleSync}
                  disabled={syncing}
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  Synchroniser
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDisconnect}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Déconnecter
                </Button>
              </div>
            )}
          </div>

          {/* Info Banner */}
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Mode Simulation</p>
                <p className="text-sm text-blue-700">
                  Les données affichées sont simulées. En production, elles seront synchronisées avec l'API Enedis DataConnect après obtention de l'agrément.
                </p>
              </div>
            </CardContent>
          </Card>

          {!meter ? (
            /* Connection Form */
            <Card className="max-w-xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="w-5 h-5" />
                  Connecter mon compteur Linky
                </CardTitle>
                <CardDescription>
                  Accédez à vos données de consommation en temps réel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleConnect} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom du compteur</Label>
                    <Input
                      id="name"
                      value={connectForm.name}
                      onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                      placeholder="Mon compteur Linky"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse du logement *</Label>
                    <Input
                      id="address"
                      value={connectForm.address}
                      onChange={(e) => setConnectForm({...connectForm, address: e.target.value})}
                      placeholder="123 rue de l'Énergie, 75001 Paris"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type de contrat</Label>
                      <Select 
                        value={connectForm.contract_type}
                        onValueChange={(v) => setConnectForm({...connectForm, contract_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BASE">Base</SelectItem>
                          <SelectItem value="HC/HP">Heures Creuses</SelectItem>
                          <SelectItem value="TEMPO">Tempo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Puissance souscrite</Label>
                      <Select 
                        value={connectForm.subscribed_power}
                        onValueChange={(v) => setConnectForm({...connectForm, subscribed_power: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 kVA</SelectItem>
                          <SelectItem value="6">6 kVA</SelectItem>
                          <SelectItem value="9">9 kVA</SelectItem>
                          <SelectItem value="12">12 kVA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={connecting}
                  >
                    {connecting ? (
                      <div className="loader w-5 h-5" />
                    ) : (
                      <>
                        <PlugZap className="w-5 h-5 mr-2" />
                        Connecter mon compteur
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            /* Dashboard */
            <>
              {/* Realtime Power */}
              {realtime && (
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <Card className="md:col-span-2 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">Puissance instantanée</p>
                          <p className="text-4xl font-bold mt-1">
                            {realtime.power_w} W
                          </p>
                          <p className="text-green-100 text-sm mt-2">
                            {realtime.power_kw} kW sur {realtime.max_power_kva} kVA
                          </p>
                        </div>
                        <div className="relative">
                          <Gauge className="w-16 h-16 text-white/30" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-bold">{realtime.usage_percent}%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-muted-foreground text-sm">PDL</p>
                      <p className="font-mono text-lg mt-1">{meter.pdl}</p>
                      <Badge className="mt-2 bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Connecté
                      </Badge>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-muted-foreground text-sm">Dernière synchro</p>
                      <p className="font-medium mt-1">
                        {new Date(meter.last_sync).toLocaleString('fr-FR')}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {meter.contract_type} - {meter.subscribed_power} kVA
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Estimated Appliances */}
              {realtime?.estimated_appliances?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Appareils en fonctionnement (estimation)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {realtime.estimated_appliances.map((app, i) => (
                        <Badge key={i} variant="outline" className="py-1.5 px-3">
                          {app.name === 'Réfrigérateur' && <Thermometer className="w-3 h-3 mr-1" />}
                          {app.name === 'Éclairage' && <Lightbulb className="w-3 h-3 mr-1" />}
                          {app.name === 'TV/Multimédia' && <Tv className="w-3 h-3 mr-1" />}
                          {app.name}: {app.power_w}W
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabs */}
              <Tabs defaultValue="history" className="space-y-6">
                <TabsList>
                  <TabsTrigger value="history">Historique</TabsTrigger>
                  <TabsTrigger value="daily">Courbe de charge</TabsTrigger>
                  <TabsTrigger value="comparison">Comparaison</TabsTrigger>
                </TabsList>

                {/* History Tab */}
                <TabsContent value="history">
                  <div className="grid lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle>Consommation sur 30 jours</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis unit=" kWh" tick={{ fontSize: 10 }} />
                              <Tooltip 
                                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                formatter={(value) => [`${value} kWh`]}
                              />
                              <Legend />
                              <Bar dataKey="peak" name="Heures pleines" fill="#f59e0b" stackId="a" />
                              <Bar dataKey="offPeak" name="Heures creuses" fill="#3b82f6" stackId="a" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="space-y-4">
                      <Card>
                        <CardContent className="p-6">
                          <p className="text-muted-foreground text-sm">Total période</p>
                          <p className="text-3xl font-bold text-foreground">
                            {consumption?.summary?.total_kwh || 0} kWh
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            ~{consumption?.summary?.average_daily_kwh || 0} kWh/jour
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="p-6">
                          <p className="text-muted-foreground text-sm">Coût estimé (Base)</p>
                          <p className="text-3xl font-bold text-foreground">
                            {consumption?.summary?.estimated_cost_base || 0}€
                          </p>
                          <p className="text-sm text-green-600 mt-1">
                            HC/HP: {consumption?.summary?.estimated_cost_hphc || 0}€
                            {consumption?.summary?.savings_hphc > 0 && (
                              <span className="ml-1">
                                (-{consumption?.summary?.savings_hphc}€)
                              </span>
                            )}
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-muted-foreground">Jour min</span>
                            <span className="font-medium">{consumption?.summary?.min_day?.kwh} kWh</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Jour max</span>
                            <span className="font-medium">{consumption?.summary?.max_day?.kwh} kWh</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                {/* Daily Curve Tab */}
                <TabsContent value="daily">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Courbe de charge détaillée</CardTitle>
                        <div className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={selectedDate || ''}
                            onChange={(e) => fetchDailyDetail(e.target.value)}
                            className="w-auto"
                            max={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {dailyDetail ? (
                        <>
                          <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground">Total</p>
                              <p className="text-2xl font-bold">{dailyDetail.total_kwh} kWh</p>
                            </div>
                            <div className="text-center p-4 bg-orange-50 rounded-lg">
                              <p className="text-sm text-orange-600">Heures pleines</p>
                              <p className="text-2xl font-bold text-orange-600">{dailyDetail.peak_kwh} kWh</p>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                              <p className="text-sm text-blue-600">Heures creuses</p>
                              <p className="text-2xl font-bold text-blue-600">{dailyDetail.off_peak_kwh} kWh</p>
                            </div>
                          </div>
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={curveData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                                <YAxis unit=" kWh" tick={{ fontSize: 10 }} />
                                <Tooltip 
                                  contentStyle={{ borderRadius: '8px' }}
                                  formatter={(value) => [`${value} kWh`]}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="kwh" 
                                  stroke="#22c55e" 
                                  fill="#22c55e" 
                                  fillOpacity={0.3}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-12">
                          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">
                            Sélectionnez une date pour voir la courbe de charge
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Comparison Tab */}
                <TabsContent value="comparison">
                  {consumption?.comparison && (
                    <div className="grid lg:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Comparaison nationale</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">Votre conso</p>
                              <p className="text-3xl font-bold text-primary">
                                {consumption.comparison.user_avg_daily_kwh} kWh/j
                              </p>
                            </div>
                            <div className="text-2xl text-muted-foreground">vs</div>
                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">Moyenne France</p>
                              <p className="text-3xl font-bold text-muted-foreground">
                                {consumption.comparison.national_avg_daily_kwh} kWh/j
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-center p-6 rounded-xl bg-muted/50">
                            <span className="text-4xl">{consumption.comparison.ranking_emoji}</span>
                            <p className="text-xl font-bold mt-2">{consumption.comparison.ranking}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {consumption.comparison.ranking_description}
                            </p>
                            <Badge 
                              className={`mt-3 ${
                                consumption.comparison.difference_percent < 0 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}
                            >
                              {consumption.comparison.difference_percent < 0 ? (
                                <TrendingDown className="w-3 h-3 mr-1" />
                              ) : (
                                <TrendingUp className="w-3 h-3 mr-1" />
                              )}
                              {consumption.comparison.difference_percent > 0 ? '+' : ''}
                              {consumption.comparison.difference_percent}%
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-yellow-500" />
                            Conseils personnalisés
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {consumption.comparison.tips?.map((tip, i) => (
                              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                <p className="text-sm">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default LinkyPage;
